/**
 * `storeOrders.*` — Store Admin order book (Commerce, M3).
 *
 * A store order is a bulk order against the wholesale catalog. Until the buyer
 * storefront ships, orders arrive by seed rather than by checkout — the API and
 * the tables are real either way.
 *
 * **Status vocabulary.** The shipped enum (migration 0134) is
 * `draft · confirmed · approved · fulfilled · rejected`. The wireframe's
 * Invoiced / Dispatched / Closed tabs are *not* states of this table — the deck
 * describes them as echoed back from the ERP invoice chain, which needs the
 * proforma hand-off. `pastDispatch` is derived from `dispatchBy`, never stored.
 *
 * @see packages/domain/src/commerce/store · docs/spec/22-store-orders.md
 */
import { z } from "zod";
import { uuid, paged, moneyPaise } from "../_common.js";

export const orderStatus = z.enum(["draft", "confirmed", "approved", "fulfilled", "rejected"]);
export type OrderStatus = z.infer<typeof orderStatus>;

/**
 * What kind of commitment this is (spec 16 R1). `at_once` ships from stock;
 * `prebook` books an upcoming season for later delivery.
 *
 * Defined here rather than in `_common.ts` because it is commerce vocabulary,
 * not a cross-cutting primitive — and `store-quotes.ts` imports it from here so
 * a quote and the order it becomes can never disagree about the kind (R6).
 */
export const orderKind = z.enum(["at_once", "prebook"]);
export type OrderKind = z.infer<typeof orderKind>;

export const orderRow = z.object({
  orderId: uuid,
  /** Allocated on confirm; empty string while the order is still a draft. */
  docNumber: z.string(),
  status: orderStatus,
  /** Derived: promised date has passed and the order isn't closed out. */
  pastDispatch: z.boolean(),
  customerId: uuid,
  customerName: z.string(),
  customerGstin: z.string().nullable(),
  /** Units across every line. Summed from the lines, never stored. */
  qty: z.number().int().nonnegative(),
  subtotalPaise: moneyPaise,
  discountPaise: moneyPaise,
  taxPaise: moneyPaise,
  totalPaise: moneyPaise,
  shipToText: z.string().nullable(),
  /**
   * `yyyy-mm-dd` — a DATE column, deliberately not a timestamp. Null on a
   * pre-book order, which promised a delivery *window* instead; read
   * `effectiveDispatchBy` when you want "the date this order is judged against".
   */
  dispatchBy: z.string().nullable(),
  /**
   * Spec 16 R5 — `dispatchBy` for at-once, `deliveryEndsOn` for pre-book.
   * Derived server-side so the Dispatch-by column and the `pastDispatch` flag
   * can never disagree about which date they mean.
   */
  effectiveDispatchBy: z.string().nullable(),
  buyerNotes: z.string().nullable(),
  /** Spec 16 R1 — `at_once` for everything placed before pre-book existed. */
  kind: orderKind,
  /** The season booked — pre-book only. */
  sourceCollectionId: uuid.nullable(),
  sourceCollectionName: z.string().nullable(),
  /** Lets the Season link render "(archived)" without a second query. */
  sourceCollectionStatus: z.enum(["active", "archived"]).nullable(),
  /** The promised delivery window, `yyyy-mm-dd` — pre-book only. */
  deliveryStartsOn: z.string().nullable(),
  deliveryEndsOn: z.string().nullable(),
  /** The quote this order was converted from, if any. */
  sourceQuoteId: uuid.nullable(),
  /** The proforma this order raised on approve (spec 38). Null before that. */
  proformaId: uuid.nullable(),
  /**
   * That proforma's number. Empty string until it is finalized — numbers are
   * allocated on issue — so the screen says "Draft proforma" rather than
   * rendering nothing.
   */
  proformaDocNumber: z.string().nullable(),
  /** Who entered it — the list's "Rep" column. */
  repName: z.string().nullable(),
  createdAt: z.string().datetime(),
});

export const orderLine = z.object({
  id: uuid,
  position: z.number().int(),
  /** The item's name, or the free-text description for a line whose item is gone. */
  name: z.string(),
  sku: z.string().nullable(),
  /** Number of packs (spec 13, R7). */
  qty: z.number().int(),
  /** The pack this line was placed against; null for legacy piece-era lines. */
  packName: z.string().nullable(),
  /** Pieces in one pack — `qty × packPieces` is the pieces shown. */
  packPieces: z.number().int().nullable(),
  capturedPricePaise: moneyPaise,
  discountBps: z.number().int(),
  taxBps: z.number().int(),
  lineTotalPaise: moneyPaise,
});

// ── list ──────────────────────────────────────────────────────────
export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  q: z.string().optional(),
  /** Omitted = every status (the "All" tab). */
  status: orderStatus.optional(),
  /**
   * Omitted = every kind (the "All" chip). Composes with `status`: the two are
   * independent facets, so "confirmed pre-books" is a question you can ask.
   */
  kind: orderKind.optional(),
});
export const listOutput = paged(orderRow);

// ── counts (tab badges + the page subtitle) ───────────────────────
export const countsOutput = z.object({
  all: z.number().int().nonnegative(),
  draft: z.number().int().nonnegative(),
  confirmed: z.number().int().nonnegative(),
  approved: z.number().int().nonnegative(),
  fulfilled: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
  /** draft + confirmed + approved — what the subtitle calls "open". */
  open: z.number().int().nonnegative(),
  pastDispatch: z.number().int().nonnegative(),
  /** Value of the open order book — the subtitle's "in flight". */
  inFlightPaise: moneyPaise,
  /**
   * Spec 16 AC7 — the kind chips. **Status-blind**, exactly as the status counts
   * are kind-blind: two independent facets, not a matrix. So selecting a kind
   * never moves the status tab counts, and vice versa.
   */
  atOnce: z.number().int().nonnegative(),
  prebook: z.number().int().nonnegative(),
});

// ── get ───────────────────────────────────────────────────────────
export const getInput = z.object({ orderId: uuid });
export const getOutput = orderRow.extend({ lines: z.array(orderLine) });

/**
 * Recent orders touching one listing — the product detail page's Orders card
 * (drafts included; an operator asking "who's buying this?" wants the whole
 * pipeline, not just paper). Qty is the order's packs of THIS listing only.
 *
 * Deliberately a bounded top-N widget, NOT a cursor-paginated list (coding
 * rule 8 exemption, PR #275 review L2): the card shows the most recent
 * handful and links into the Orders module for anything more.
 */
export const listForListingInput = z.object({
  listingId: uuid,
  limit: z.number().int().min(1).max(20).default(5),
});
export const listForListingOutput = z.object({
  items: z.array(
    z.object({
      orderId: uuid,
      /** Null until the order leaves draft (spec 22 R28). */
      docNumber: z.string().nullable(),
      status: orderStatus,
      /** Packs of this listing on the order (all its lines summed). */
      packs: z.number().int(),
      createdAt: z.string(),
    }),
  ),
});

// ── transition ────────────────────────────────────────────────────
export const transitionInput = z.object({
  orderId: uuid,
  status: orderStatus,
  reason: z.string().trim().max(500).optional(),
});
export const transitionOutput = z.object({
  orderId: uuid,
  status: orderStatus,
  /**
   * Set only on the `→ approved` edge, which creates the proforma (spec 38);
   * every other transition omits it.
   *
   * Returned so a caller *can* act on the new proforma without a refetch. The
   * order-detail screen doesn't need to — it invalidates and re-reads
   * `proformaId` off the order — so this is currently unused by the web client.
   */
  proformaId: uuid.optional(),
});

export type ListOrdersInput = z.infer<typeof listInput>;
export type TransitionOrderInput = z.infer<typeof transitionInput>;
